import React from 'react';
import { observer } from "mobx-react-lite";
import { useTranslation } from 'react-i18next';
import { StringActionDetail } from './components/StringActionDetail';
import { NumberActionDetail } from './components/NumberActionDetail';
import { ItemSelectionActionDetail } from './components/ItemSelectionActionDetail';
import { FactionSelectionActionDetail } from './components/FactionSelectionActionDetail';
import { AnimationElementValueModel, AreaTriggerValueModel, EnemyOnMapValueModel, ExtendedMapMarkerValueModel, extendedMapMarkerValueModelTypesArray, FactionValueModel, InteractionTriggerValueModel, ItemIdValueModel, ItemTagValueModel, MapMarkerValueModel, NPCOnMapValueModel, NPCValueModel, NumberValueModel, PlayStyleValueModel, QuestIdValueModel, SoundValueModel, StringValueModel, TranslatedStringValueModel } from '../../../../shared/action/ValueModel';
import { DisplayMode, TranslatedStringActionDetail } from './components/TranslatedStringActionDetail';
import { undoableActionEditorSelectActionTreeHierachy } from '../../../stores/undo/operation/ActionEditorSelectActionTreeHierarchy';
import { undoableActionEditorCreateTemplate } from '../../../stores/undo/operation/ActionEditorCreateTemplateOp';
import { PlayStyleSelectionActionDetail } from './components/PlayStyleSelectionActionDetail';
import { ItemSelectionMode } from '../../../../shared/action/ActionModel';
import { Orientation, PopupSubMenu, PopupSubMenuIconContainer } from '../../menu/PopupSubMenu';
import styled from 'styled-components';
import { IoMdHelpCircle } from 'react-icons/io';
import { NPCActionDetail } from './components/NPCSelectionActionDetail';
import { AnimationElementSelectionDetail } from './components/AnimationElementSelectionDetail';
import { QuestSelectionDetail } from './components/QuestSelectionDetail';
import { InteractionTriggerSelectionDetail } from './components/InteractionElementSelectionDetail';
import { MapElementSelectionDetail } from './components/MapElementSelectionDetail';
import { MapElementFilter } from "../../../helper/MapElementFilter";
import { passComplexityGate } from '../../../../shared/definitions/other/EditorComplexity';
import { actionEditorStore } from '../../../stores/ActionEditorStore';
import { gameStore } from '../../../stores/GameStore';
import { localSettingsStore } from '../../../stores/LocalSettingsStore';
import { AnimationElementWithAnimationRestrictionsSelectionDetail as AnimationElementWithRequiredAnimationsSelectionDetail } from './components/AnimationElementWithRequiredAnimationsSelectionDetail';
import { SoundSelectionButton } from "../selector/SoundSelectionButton";
import { ElementGroup } from './components/BaseElements';
const HelpIcon = styled(IoMdHelpCircle).withConfig({
  displayName: "ActionTreeDetails__HelpIcon",
  componentId: "sc-1cexoee-0"
})(["vertical-align:bottom;font-size:18px;"]);
const FormattingHelpButtonContainer = styled.div.withConfig({
  displayName: "ActionTreeDetails__FormattingHelpButtonContainer",
  componentId: "sc-1cexoee-1"
})(["float:right;margin-right:8px;"]);
export const ActionTreeDetails = observer(({
  action
}) => {
  const {
    t
  } = useTranslation();
  const {
    languageKey
  } = gameStore;
  const {
    isProductionEditor,
    editorComplexity
  } = localSettingsStore;
  const subTreeProps = action.treePropertiesAction;
  const parentTree = action.parentTree;

  const createAndOpenNewTemplate = async () => {
    undoableActionEditorCreateTemplate(action);
  };

  const focusOnTree = () => {
    undoableActionEditorSelectActionTreeHierachy([action.$modelId]);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormattingHelpButtonContainer, null, /*#__PURE__*/React.createElement(PopupSubMenu, {
    orientation: Orientation.Left,
    relativeOffset: 0,
    buttonContent: /*#__PURE__*/React.createElement(PopupSubMenuIconContainer, null, /*#__PURE__*/React.createElement(HelpIcon, null)),
    containerWidth: "450px",
    positionFixed: true
  }, action.treePropertiesAction.tutorial.get(languageKey))), isProductionEditor && /*#__PURE__*/React.createElement(ElementGroup, null, /*#__PURE__*/React.createElement("button", {
    onClick: createAndOpenNewTemplate
  }, t("action_editor.tree_copy_to_template"))), !isProductionEditor && !actionEditorStore.treeFocussedForWorkshop && passComplexityGate(subTreeProps.complexityGate, editorComplexity) && /*#__PURE__*/React.createElement(ElementGroup, null, /*#__PURE__*/React.createElement("button", {
    onClick: () => focusOnTree()
  }, t("action_editor.tree_focus_for_workshop"))), action.treeParameterActions().map((param, index) => /*#__PURE__*/React.createElement("div", {
    key: index
  }, param.dividingLine && /*#__PURE__*/React.createElement("hr", null), param.tutorial.get(languageKey) && /*#__PURE__*/React.createElement(FormattingHelpButtonContainer, null, /*#__PURE__*/React.createElement(PopupSubMenu, {
    orientation: Orientation.Left,
    relativeOffset: 0,
    buttonContent: /*#__PURE__*/React.createElement(PopupSubMenuIconContainer, null, /*#__PURE__*/React.createElement(HelpIcon, null)),
    containerWidth: "450px",
    positionFixed: true
  }, param.tutorial.get(languageKey))), param.value instanceof StringValueModel && /*#__PURE__*/React.createElement(StringActionDetail, {
    name: param.description.get(languageKey),
    value: param.value.get(),
    valueSetter: param.value.set.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof NumberValueModel && /*#__PURE__*/React.createElement(NumberActionDetail, {
    name: param.description.get(languageKey),
    value: param.value.get(),
    valueSetter: param.value.set.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof ItemIdValueModel && /*#__PURE__*/React.createElement(ItemSelectionActionDetail, {
    parentTree: parentTree,
    name: param.description.get(languageKey),
    selectionMode: ItemSelectionMode.Item,
    selectionModeSetter: null,
    itemIdOrTags: param.value.get(),
    itemIdOrTagsSetter: param.value.set.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof ItemTagValueModel && /*#__PURE__*/React.createElement(ItemSelectionActionDetail, {
    parentTree: parentTree,
    name: param.description.get(languageKey),
    selectionMode: ItemSelectionMode.ItemWithOneTag,
    selectionModeSetter: null,
    itemIdOrTags: param.value.get(),
    itemIdOrTagsSetter: param.value.set.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof QuestIdValueModel && /*#__PURE__*/React.createElement(QuestSelectionDetail, {
    parentTree: parentTree,
    name: param.description.get(languageKey),
    questId: param.value.get(),
    questIdSetter: param.value.set.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof NPCValueModel && /*#__PURE__*/React.createElement(NPCActionDetail, {
    parentTree: parentTree,
    name: param.description.get(languageKey),
    selectedNPC: param.value.value,
    npcSetter: param.value.setValue.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof NPCOnMapValueModel && /*#__PURE__*/React.createElement(MapElementSelectionDetail, {
    name: param.description.get(languageKey),
    selectedElement: param.value.value,
    elementSetter: param.value.setValue.bind(param.value),
    parameterTypes: ["actions/NPCOnMapValueModel", "actions/EnemyOnMapValueModel"],
    getSelectableElements: MapElementFilter.filterNPCLabels,
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof EnemyOnMapValueModel && /*#__PURE__*/React.createElement(MapElementSelectionDetail, {
    name: param.description.get(languageKey),
    selectedElement: param.value.value,
    elementSetter: param.value.setValue.bind(param.value),
    parameterTypes: ["actions/EnemyOnMapValueModel"],
    getSelectableElements: MapElementFilter.filterEnemiesLabels,
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof FactionValueModel && /*#__PURE__*/React.createElement(FactionSelectionActionDetail, {
    parentTree: parentTree,
    name: param.description.get(languageKey),
    value: param.value.get(),
    valueSetter: param.value.set.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof PlayStyleValueModel && /*#__PURE__*/React.createElement(PlayStyleSelectionActionDetail, {
    parentTree: parentTree,
    name: param.description.get(languageKey),
    value: param.value.get(),
    valueSetter: param.value.set.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof TranslatedStringValueModel && /*#__PURE__*/React.createElement(TranslatedStringActionDetail, {
    name: param.description.get(languageKey),
    translatedString: param.value.value,
    displayMode: DisplayMode.CommentAndGenders,
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof MapMarkerValueModel && /*#__PURE__*/React.createElement(MapElementSelectionDetail, {
    name: param.description.get(languageKey),
    selectedElement: param.value.value,
    elementSetter: param.value.setValue.bind(param.value),
    parameterTypes: ["actions/MapMarkerValueModel"],
    getSelectableElements: MapElementFilter.filterMapMarkerLabels,
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof ExtendedMapMarkerValueModel && /*#__PURE__*/React.createElement(MapElementSelectionDetail, {
    name: param.description.get(languageKey),
    selectedElement: param.value.value,
    elementSetter: param.value.setValue.bind(param.value),
    parameterTypes: extendedMapMarkerValueModelTypesArray,
    getSelectableElements: MapElementFilter.filterExtendedMapMarkerLabels,
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof AreaTriggerValueModel && /*#__PURE__*/React.createElement(MapElementSelectionDetail, {
    name: param.description.get(languageKey),
    selectedElement: param.value.value,
    elementSetter: param.value.setValue.bind(param.value),
    parameterTypes: ["actions/AreaTriggerValueModel"],
    getSelectableElements: MapElementFilter.filterTriggerLabels,
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof AnimationElementValueModel && (param.value.hasRequiredAnimationNames ? /*#__PURE__*/React.createElement(AnimationElementWithRequiredAnimationsSelectionDetail, {
    name: param.description.get(languageKey),
    selectedAnimationElement: param.value.value,
    animationElementSetter: param.value.setValue.bind(param.value),
    requiredAnimationNames: param.value.requiredAnimationNames,
    allowBlankValue: param.allowBlankValue
  }) : /*#__PURE__*/React.createElement(AnimationElementSelectionDetail, {
    name: param.description.get(languageKey),
    selectedAnimationElement: param.value.value,
    animationElementSetter: param.value.setValue.bind(param.value),
    allowBlankValue: param.allowBlankValue
  })), param.value instanceof InteractionTriggerValueModel && /*#__PURE__*/React.createElement(InteractionTriggerSelectionDetail, {
    name: param.description.get(languageKey),
    selectedInteractionTrigger: param.value.value,
    interactionTriggerSetter: param.value.setValue.bind(param.value),
    allowBlankValue: param.allowBlankValue
  }), param.value instanceof SoundValueModel && /*#__PURE__*/React.createElement(SoundSelectionButton, {
    onSelectSoundPath: soundPath => {
      const soundValueModel = param.value;
      soundValueModel.setValue(soundPath);
      soundValueModel.setTreeParameter(null);
    },
    selectedPath: param.value.value,
    text: param.description.get(languageKey),
    action: action,
    selectedTreeParameter: param.value.treeParameter,
    onSelectTreeParameter: treeParameter => {
      const soundValueModel = param.value;
      soundValueModel.setTreeParameter(treeParameter);
      soundValueModel.setValue(null);
    },
    allowBlankValue: param.allowBlankValue
  }))));
});